// The fold engine — NP-2: arrangement = f(width, chain, engagement, address,
// recency, pins). A pure projection of the address chain into rails. Nothing is
// stored; the rails re-derive every render. Opening past the width law folds the
// least-recently-addressed rail into a spine (NP-5). Address, pins, and peers
// order the survivors; none of them can manufacture physical width.

import { KIND_GLYPH } from "./types"
import type {
  FoldInput,
  DeskFurniture,
  Engagement,
  MountChip,
  Rail,
  RailTab,
  RailVoice,
  RollUp,
  View,
  VoiceLine,
} from "./types"

// A primitive tab is a session or an eye — the self-similar thing you navigate.
const PRIMITIVE = new Set(["session", "eye"])

export const RAIL_CAP_DEFAULT = 2
export const RAIL_CAP_MIN = 1
export const RAIL_CAP_MAX = 4

// These are the rendered desk tracks, not breakpoint guesses. The allocator
// and the CSS consume the same furniture contract: two fixed side rails, one
// protected session view, and every projected agent rail as either a full column
// or a spine rib. Opening a rail spends only the delta from rib → column.
export const EGO_RAIL_PX = 300
export const CONTEXT_RAIL_PX = 280
export const AGENT_RAIL_PX = 268
export const AGENT_SPINE_PX = 26
export const DECK_MIN_PX = 560

export function appliedStageWidth(requestedWidth: number, availableWidth: number): number {
  const requested = Number.isFinite(requestedWidth) ? Math.max(0, Math.floor(requestedWidth)) : 0
  const available = Number.isFinite(availableWidth) ? Math.max(0, Math.floor(availableWidth)) : 0
  return Math.min(requested, available)
}

export function minimumStageWidth(totalRails: number): number {
  const rails = Math.max(0, Math.trunc(totalRails))
  return EGO_RAIL_PX + CONTEXT_RAIL_PX + DECK_MIN_PX + rails * AGENT_SPINE_PX
}

// The physical fit at a measured stage width. This may only LOWER the
// person's cap; a wider door never silently expands the attention surface.
// One formula for every furniture choice — fitsByWidth is the full-furniture
// reading of it (DL4: one allocator, never a second copy). Exported because
// consumers outside this tree (cleo-code's room room) read the same formula
// in their own frame: a room whose stage is measured inside the shell row
// passes zero side terms, and a room with a person-resizable rail passes its
// live rail width — the 268 default preserves every donor call site.
export function fitsWithSides(
  width: number,
  totalRails: number,
  egoPx: number,
  contextPx: number,
  agentRailPx: number = AGENT_RAIL_PX,
): number {
  const rails = Math.max(0, Math.trunc(totalRails))
  if (rails === 0) return 0
  const minimum = egoPx + contextPx + DECK_MIN_PX + rails * AGENT_SPINE_PX
  const openDelta = Math.max(1, Math.floor(agentRailPx) - AGENT_SPINE_PX)
  return Math.min(rails, Math.max(0, Math.floor((Math.floor(width) - minimum) / openDelta)))
}

export function fitsByWidth(width: number, totalRails = RAIL_CAP_MAX): number {
  return fitsWithSides(width, totalRails, EGO_RAIL_PX, CONTEXT_RAIL_PX)
}

// The desk furniture ladder (CR4b, decided 2026-08-23 by principal
// delegation): before the desk surrenders to Pocket, the fixed side rails
// fold to ribs — the context rail first, Cleo's ego rail last ("Last resort
// for the root: Cleo's rail becomes a spine rather than vanishing", 018 fold
// law; A8: "Direct folds preserve a visible spine"). Pocket remains for true
// phone-class: width ≤ 480, or even both ribs cannot open one column.
export const SIDE_RIB_PX = 26
export const POCKET_HARD_WIDTH_PX = 480

// A side rail as the ladder sees it: present with a full width and a rib to
// fold to, or absent (null) in a room that never built that rail.
export interface SideRailSpec {
  fullPx: number
  ribPx?: number
}

export interface SideFurniture {
  egoPx: number
  contextPx: number
  egoFolded: boolean
  contextFolded: boolean
  // Whether the returned step actually holds the width law. False means the
  // ladder is exhausted at its bottom step — the caller decides what yields
  // (this desk goes to Pocket; a Pocket-less room keeps the ribs).
  fits: boolean
  widthBonus: number
}

// The ladder walk itself (CR4b/FD24), furniture-agnostic: side rails fold to
// ribs in the fixed order — context first, ego last — and an absent rail
// contributes no step and no pixels. A step holds when one agent column can
// open at that furniture; with zero rails there is no column to open, so the
// step holds when the protected session view alone still fits. One ladder for
// every room, never a second copy.
export function sideFurnitureLadder(
  width: number,
  totalRails: number,
  sides: { ego: SideRailSpec | null; context: SideRailSpec | null },
  agentRailPx: number = AGENT_RAIL_PX,
): SideFurniture {
  const w = Math.floor(width)
  const rails = Math.max(0, Math.trunc(totalRails))
  const egoFull = sides.ego?.fullPx ?? 0
  const egoRib = sides.ego ? (sides.ego.ribPx ?? SIDE_RIB_PX) : 0
  const contextFull = sides.context?.fullPx ?? 0
  const contextRib = sides.context ? (sides.context.ribPx ?? SIDE_RIB_PX) : 0
  const steps = [
    { egoPx: egoFull, contextPx: contextFull },
    ...(sides.context ? [{ egoPx: egoFull, contextPx: contextRib }] : []),
    ...(sides.ego ? [{ egoPx: egoRib, contextPx: contextRib }] : []),
  ]
  const holds = (egoPx: number, contextPx: number): boolean =>
    rails === 0
      ? w >= egoPx + contextPx + DECK_MIN_PX
      : fitsWithSides(w, rails, egoPx, contextPx, agentRailPx) >= 1
  // The ladder's bottom is both ribs (an absent side's rib is 0px) — stated
  // directly so the exhausted-ladder fallback is total without indexing.
  const step =
    steps.find((s) => holds(s.egoPx, s.contextPx)) ?? { egoPx: egoRib, contextPx: contextRib }
  return {
    egoPx: step.egoPx,
    contextPx: step.contextPx,
    egoFolded: sides.ego !== null && step.egoPx !== egoFull,
    contextFolded: sides.context !== null && step.contextPx !== contextFull,
    fits: holds(step.egoPx, step.contextPx),
    widthBonus: egoFull - step.egoPx + (contextFull - step.contextPx),
  }
}

// The inverse reading of the ladder's first step: the widest full context
// column that still HOLDS at this width. This is the resize interaction's
// ceiling — a person's drag may never cross into the fold the same formula
// would immediately declare (the fold is the stage's verdict on the window,
// never a trap sprung by the handle). Derived from fitsWithSides' own terms
// so the bound and the fold can never disagree; zero rails holds when the
// protected session view alone still fits, the same reading the ladder walks.
export function maxContextFullPx(
  width: number,
  totalRails: number,
  egoPx: number,
  agentRailPx: number = AGENT_RAIL_PX,
): number {
  const w = Math.floor(width)
  const rails = Math.max(0, Math.trunc(totalRails))
  if (rails === 0) return w - egoPx - DECK_MIN_PX
  const openDelta = Math.max(1, Math.floor(agentRailPx) - AGENT_SPINE_PX)
  return w - egoPx - DECK_MIN_PX - rails * AGENT_SPINE_PX - openDelta
}

// Widen Cleo (dated 2026-10-07, the principal: "I agree with your
// recommendation"): the same ladder read with the ego asking for more than
// its fixed column. This is the only act that may fold the dock. The yield
// order, first to last: the context column narrows to its floor and folds to
// its rib (the context's own law, reading the ego width returned here), the
// dock renders tight, the first nested column folds to its spine (the context
// law holds one while any nested rail exists), and only then does the ego
// settle below its wish, never below its fixed column. The deck minimum is
// never spent, and nothing here is stored: turning the wish off re-derives
// the ordinary ladder.
export interface EgoWishInput {
  /** The whole furniture row: dock + ego + nested rails + deck + context. */
  readonly width: number
  /** NESTED rails, each at least a spine. */
  readonly totalRails: number
  readonly agentRailPx?: number
  /** The ego's fixed column, and the wider column it asks for. */
  readonly ego: { readonly basePx: number; readonly wishPx: number }
  /** The context rail's folded floor (its rib); null when the row has none. */
  readonly contextRibPx: number | null
  /** The dock's preferred and tight widths; null when the row has none. */
  readonly dock: { readonly fullPx: number; readonly tightPx: number } | null
}

export interface EgoWishFurniture {
  readonly egoPx: number
  readonly dockPx: number
  readonly dockTight: boolean
  // The widest the dock may be dragged while this step stands (the ego and
  // the held column keep their widths); its own width once it has yielded.
  readonly dockMaxPx: number
  // Whether the deck minimum holds at this step. False only when even the
  // fixed column cannot stand beside a rib, a tight dock and every spine.
  readonly fits: boolean
}

export function egoWishLadder(input: EgoWishInput): EgoWishFurniture {
  const w = Math.floor(input.width)
  const rails = Math.max(0, Math.trunc(input.totalRails))
  const openDelta = Math.max(1, Math.floor(input.agentRailPx ?? AGENT_RAIL_PX) - AGENT_SPINE_PX)
  const base = Math.max(0, Math.floor(input.ego.basePx))
  const wish = Math.max(base, Math.floor(input.ego.wishPx))
  const rib = input.contextRibPx ?? 0
  const dockFull = input.dock === null ? 0 : Math.floor(input.dock.fullPx)
  const dockTight = input.dock === null ? 0 : Math.min(dockFull, Math.floor(input.dock.tightPx))
  // What the deck holds beyond its minimum, with the context at its rib and
  // `held` nested columns open beside every other nested spine.
  const spare = (dockPx: number, egoPx: number, held: number): number =>
    w - dockPx - rib - egoPx - DECK_MIN_PX - rails * AGENT_SPINE_PX - held * openDelta
  const held = rails > 0 ? 1 : 0
  const at = (egoPx: number, dockPx: number, heldPx: number): EgoWishFurniture => ({
    egoPx,
    dockPx,
    dockTight: dockPx !== dockFull,
    dockMaxPx: dockPx !== dockFull ? dockPx : Math.max(dockPx, dockPx + spare(dockPx, egoPx, heldPx)),
    fits: spare(dockPx, egoPx, 0) >= 0,
  })
  // No wish: the ordinary ladder. The dock yields to nothing else.
  if (wish === base) return { ...at(base, dockFull, 0), dockMaxPx: Number.POSITIVE_INFINITY }
  if (spare(dockFull, wish, held) >= 0) return at(wish, dockFull, held)
  if (spare(dockTight, wish, held) >= 0) return at(wish, dockTight, held)
  // Even a tight dock cannot keep the held column beside the wish, so the
  // column folds to its spine; the dock is asked again only if the wish still
  // needs it (a fold that buys nothing is undone).
  if (spare(dockFull, wish, 0) >= 0) return at(wish, dockFull, 0)
  const settled = Math.max(base, Math.min(wish, wish + spare(dockTight, wish, 0)))
  // A tight dock that buys the ego nothing stays as the person left it.
  return settled === base ? at(base, dockFull, 0) : at(settled, dockTight, 0)
}

export function deskFurniture(width: number, totalRails: number): DeskFurniture {
  const w = Math.floor(width)
  // The desk's own furniture policy rides the shared ladder. Zero rails keep
  // the historical reading — nothing can open, so the desk yields — and the
  // Pocket gate stays this function's, not the ladder's.
  if (w > POCKET_HARD_WIDTH_PX && Math.trunc(totalRails) > 0) {
    const step = sideFurnitureLadder(w, totalRails, {
      ego: { fullPx: EGO_RAIL_PX },
      context: { fullPx: CONTEXT_RAIL_PX },
    })
    if (step.fits) {
      return {
        egoPx: step.egoPx,
        contextPx: step.contextPx,
        egoFolded: step.egoFolded,
        contextFolded: step.contextFolded,
        pocket: false,
        widthBonus: step.widthBonus,
      }
    }
  }
  return {
    egoPx: EGO_RAIL_PX,
    contextPx: CONTEXT_RAIL_PX,
    egoFolded: false,
    contextFolded: false,
    pocket: true,
    widthBonus: 0,
  }
}

export function normalizeRailCap(value: number | undefined): number {
  if (!Number.isFinite(value)) return RAIL_CAP_DEFAULT
  return Math.min(RAIL_CAP_MAX, Math.max(RAIL_CAP_MIN, Math.trunc(value!)))
}

export function railBudget(
  width: number,
  railCap = RAIL_CAP_DEFAULT,
  totalRails = RAIL_CAP_MAX,
): number {
  return Math.min(normalizeRailCap(railCap), fitsByWidth(width, totalRails))
}

function engagementOf(r: FoldInput): Engagement | null {
  if (r.kind !== "session" && r.kind !== "eye") return null
  return (r.fields.engagement as Engagement) ?? "headless"
}

function childPrimitives(recs: FoldInput[], parentId: string): FoldInput[] {
  return recs
    .filter((r) => r.parentId === parentId && PRIMITIVE.has(r.kind))
    .sort((a, b) => a.bornSeq - b.bornSeq)
}

// Roll everything nested under a node up into one set of counts (NP: counts
// roll up into the tab bars). Walks the whole subtree, not just direct children.
export function rollUp(recs: FoldInput[], rootId: string): RollUp {
  const kids = recs.filter((r) => r.parentId === rootId)
  let open = 0
  let working = 0
  let held = 0
  let deep = kids.length ? 1 : 0
  for (const k of kids) {
    if (k.kind === "session" || k.kind === "eye") {
      const eng = engagementOf(k)
      if (k.fields.converged === true) {
        // converged — no longer open
      } else if (eng === "engaged") working++
      else open++
    }
    if (k.kind === "contention" && k.fields.resolved !== true) held++
    const sub = rollUp(recs, k.id)
    open += sub.open
    working += sub.working
    held += sub.held
    deep = Math.max(deep, 1 + sub.deep)
  }
  return { open, working, held, deep }
}

function handOf(r: FoldInput): string | null {
  return (r.fields.hand as string) ?? null
}

function statusWord(r: FoldInput): string {
  if (r.fields.converged === true) return "CONVERGED"
  if (r.kind === "eye") return "STREAMING"
  const eng = engagementOf(r)
  if (eng === "engaged") return "ENGAGED"
  return "WORKING"
}

function tabMeta(recs: FoldInput[], r: FoldInput, roll: RollUp): string {
  const parts = recs.filter((c) => c.parentId === r.id).length
  const inView = recs.filter((c) => c.parentId === r.id && PRIMITIVE.has(c.kind)).length
  if (r.kind === "eye") return `${statusWord(r).toLowerCase()} · ${roll.open + roll.working} nested`
  const bits: string[] = []
  bits.push(`${parts} obj in view`)
  if (inView) bits.push(`${inView} deep`)
  if (roll.working) bits.push(`${roll.working} working`)
  if (roll.held) bits.push(`${roll.held} held`)
  return bits.join(" · ")
}

// Live, unconverged primitives anywhere under a node — the roll-up's members.
function liveDescendantPrimitives(recs: FoldInput[], rootId: string): FoldInput[] {
  const out: FoldInput[] = []
  const walk = (pid: string) => {
    for (const k of recs.filter((c) => c.parentId === pid)) {
      if (PRIMITIVE.has(k.kind) && k.fields.converged !== true) out.push(k)
      walk(k.id)
    }
  }
  walk(rootId)
  return out
}

function makeTab(
  recs: FoldInput[],
  r: FoldInput,
  opts: { addressed: boolean; isLeaf: boolean; seenCut: number },
): RailTab {
  const roll = rollUp(recs, r.id)
  // Ghost chips (018 §2): an un-addressed tab's live descendants surface in
  // the strip beside it — the roll-up made touchable. An addressed tab needs
  // none: its own strip below carries them. Pointers, never seats.
  const ghosts = opts.addressed
    ? []
    : liveDescendantPrimitives(recs, r.id)
        .sort((a, b) => {
          const ea = engagementOf(a) === "engaged" ? 0 : 1
          const eb = engagementOf(b) === "engaged" ? 0 : 1
          if (ea !== eb) return ea - eb
          return a.bornSeq - b.bornSeq
        })
        .map((d) =>
          chipOf(
            recs,
            d,
            opts.seenCut,
            `rolled up — running under ${r.label.toLowerCase()}; tap to re-front the whole path down to it.`,
          ),
        )
  return {
    id: r.id,
    kind: r.kind,
    glyph: KIND_GLYPH[r.kind],
    label: r.label,
    addressed: opts.addressed,
    isLeaf: opts.isLeaf,
    engagement: engagementOf(r),
    hand: handOf(r),
    novel: r.bornSeq > opts.seenCut,
    rollup: roll,
    meta: tabMeta(recs, r, roll),
    ghosts,
  }
}

// --- The rail voice (row 55, 018 §5): the rail is a conversational column. ---

// The thread is the log records under the session, at the cut, oldest first —
// the same computation as the ledger (SL-10 parity).
export function threadOf(recs: FoldInput[], sessionId: string, seenCut: number): VoiceLine[] {
  return recs
    .filter((r) => r.kind === "log" && r.parentId === sessionId)
    .sort((a, b) => a.bornSeq - b.bornSeq)
    .map((r) => ({
      id: r.id,
      author: r.author,
      text: r.label,
      seq: r.bornSeq,
      novel: r.bornSeq > seenCut,
    }))
}

function voiceOf(
  recs: FoldInput[],
  r: FoldInput | undefined,
  seenCut: number,
): RailVoice | null {
  if (!r || !PRIMITIVE.has(r.kind)) return null
  return {
    sessionId: r.id,
    who: (r.fields.who as string) ?? r.label,
    hand: handOf(r),
    glyph: KIND_GLYPH[r.kind],
    line:
      (r.fields.line as string) ??
      (r.kind === "eye"
        ? "observing — looking writes nothing."
        : "running headless — the thread is the record; touch it and it is yours."),
    engagement: engagementOf(r),
    thread: threadOf(recs, r.id, seenCut),
    mounts: [],
    census: [],
  }
}

function chipOf(recs: FoldInput[], r: FoldInput, seenCut: number, why: string): MountChip {
  const h = handOf(r)
  return {
    id: r.id,
    hand: h && h !== "—" ? h : null, // the root's "—" is no address; fall to the label
    label: r.label,
    glyph: KIND_GLYPH[r.kind],
    kind: r.kind,
    engagement: engagementOf(r),
    novel:
      r.bornSeq > seenCut ||
      recs.some((c) => c.kind === "log" && c.parentId === r.id && c.bornSeq > seenCut),
    why,
    thread: threadOf(recs, r.id, seenCut),
  }
}

export interface FoldResult {
  rails: Rail[]
  chain: string[]
  leafId: string
  budget: number
  openCount: number
  total: number
}

export function foldArrangement(
  recs: FoldInput[],
  view: Required<Pick<View, "address" | "width" | "railCap" | "pins" | "railHistory" | "peers" | "collapsed">> &
    Pick<View, "worldName">,
  seenCut: number,
): FoldResult {
  const byId = new Map(recs.map((r) => [r.id, r]))

  // --- The chain: walk parentId from the address up to the root, top-down. ---
  const chain: string[] = []
  {
    let cur: string | null = view.address
    let guard = 0
    while (cur && byId.has(cur) && guard++ < 64) {
      chain.unshift(cur)
      cur = byId.get(cur)!.parentId
    }
  }
  const leafId = chain[chain.length - 1] ?? view.address
  const leaf = byId.get(leafId)

  // --- Rail parents: every ancestor, plus the leaf itself if it decomposes. ---
  const railParentIds = chain.slice(0, -1)
  if (leaf && childPrimitives(recs, leaf.id).length > 0) railParentIds.push(leaf.id)
  if (railParentIds.length === 0 && leaf) railParentIds.push(leaf.id) // a bare root

  const spineToLeaf = new Set(chain) // the addressed path

  const chainRails: Rail[] = railParentIds.map((pid, depth) => {
    const parent = byId.get(pid)
    const kids = childPrimitives(recs, pid)
    // the addressed tab in this rail = the next chain node under this parent
    const nextInChain = chain[chain.indexOf(pid) + 1] ?? null
    const tabs: RailTab[] = kids.map((k) =>
      makeTab(recs, k, {
        addressed: spineToLeaf.has(k.id),
        isLeaf: k.id === leafId,
        seenCut,
      }),
    )
    // The depth-0 crumb names the containing World from the view (W38); with
    // no bound World it gets honest no-name copy, never "the estate".
    const crumb =
      depth === 0
        ? `${view.worldName?.trim() || "this World"} · one graph`
        : `${(parent?.label ?? "").toLowerCase()} · root`
    return {
      id: pid,
      parentId: parent?.parentId ?? null,
      crumb,
      depth,
      state: "open" as const,
      pinned: view.pins.includes(pid),
      peer: false,
      tabs,
      addressedTabId: nextInChain,
      spineLabel: "",
      voice: null as RailVoice | null, // granted below, once fold states settle
    }
  })

  // Peer rails are the same physical furniture as chain rails. They begin as
  // candidates and earn a full column from the same measured budget; a refused
  // peer keeps a spine and its thread re-homes like any other folded rail.
  const peerRails: Rail[] = view.peers
    .filter((pid) => byId.has(pid) && !spineToLeaf.has(pid))
    .map((pid, i) => {
      const r = byId.get(pid)!
      return {
        id: `peer:${pid}`,
        parentId: r.parentId,
        crumb: `${r.label.toLowerCase()} · peer`,
        depth: chainRails.length + i,
        state: "open" as const,
        pinned: true,
        peer: true,
        tabs: [makeTab(recs, r, { addressed: true, isLeaf: false, seenCut })],
        addressedTabId: pid,
        spineLabel: "",
        voice: null as RailVoice | null,
      }
    })

  const allRails = [...chainRails, ...peerRails]
  const budget = railBudget(view.width, view.railCap, allRails.length)

  // --- The ≤N law (NP-5): the deepest/addressed rail wins, then pins/peers,
  //     then recency. The shared budget is a hard physical ceiling. A hand fold
  //     removes that rail from candidacy even when it is the deepest child; its
  //     session view remains and its voice re-homes into an open parent. ---
  const collapsed = new Set(view.collapsed)
  const deepestId = chainRails[chainRails.length - 1]?.id
  const recency = (rail: Rail): number => {
    const key = rail.addressedTabId ?? rail.id
    const i = view.railHistory.lastIndexOf(key)
    return i >= 0 ? i : rail.depth // never-navigated → deeper is more recent
  }
  const candidates = allRails
    .filter((rail) => rail.peer || !collapsed.has(rail.id))
    .sort((a, b) => {
      const addressedA = !a.peer && a.id === deepestId ? 0 : 1
      const addressedB = !b.peer && b.id === deepestId ? 0 : 1
      if (addressedA !== addressedB) return addressedA - addressedB
      const priorityA = a.pinned ? 0 : 1
      const priorityB = b.pinned ? 0 : 1
      if (priorityA !== priorityB) return priorityA - priorityB
      return recency(b) - recency(a) || b.depth - a.depth
    })
  const keepOpen = new Set(candidates.slice(0, budget).map((rail) => rail.id))

  for (const rail of allRails) {
    if (!keepOpen.has(rail.id)) {
      rail.state = "spine"
      const addressed = rail.addressedTabId ? byId.get(rail.addressedTabId) : undefined
      if (addressed) {
        const h = handOf(addressed)
        const kind = addressed.kind === "eye" ? "Eye" : "Hand"
        const obs = addressed.kind === "eye" ? "observing" : engagementOf(addressed) === "engaged" ? "engaged" : "headless"
        rail.spineLabel = `${kind} · ${h ?? "—"} · ${obs} · ${statusWord(addressed)}`
      } else {
        rail.spineLabel = byId.get(rail.id)?.label ?? "rail"
      }
    }
  }

  // --- The rail voice (row 55): open rails carry a conversational column. A
  //     spine's voice is null — its thread re-homes below. ---
  for (const rail of chainRails) {
    rail.voice = rail.state === "open" ? voiceOf(recs, byId.get(rail.id), seenCut) : null
  }
  for (const rail of peerRails) {
    const session = rail.addressedTabId ? byId.get(rail.addressedTabId) : undefined
    rail.voice = rail.state === "open" ? voiceOf(recs, session, seenCut) : null
  }

  // --- The re-homing table (rows 56–57, 018 §6): a folded level's thread
  //     seats in a host rail — four-step fallback, first match wins:
  //       1. the nearest ancestor on the chain currently open
  //       2. scanning downward — the first open level below it on the chain
  //       3. the first open rail anywhere on the chain
  //       4. the first open peer when the chain has no host
  //     The scan covers the whole session map, not just the chain. One thread,
  //     one place: merging re-homes, never duplicates, never hides. ---
  const openChain = chainRails.filter((r) => r.state === "open" && r.voice)
  const openAny = allRails.filter((r) => r.state === "open" && r.voice)
  const hostOfChainIndex = (idx: number): Rail | null => {
    for (let i = idx - 1; i >= 0; i--) {
      const rail = chainRails[i]
      if (rail !== undefined && rail.state === "open" && rail.voice) return rail
    }
    for (let i = idx + 1; i < chainRails.length; i++) {
      const rail = chainRails[i]
      if (rail !== undefined && rail.state === "open" && rail.voice) return rail
    }
    return openChain[0] ?? openAny[0] ?? null
  }

  const seated = new Set<string>() // sessions that already hold a seat
  for (const r of chainRails) if (r.state === "open" && r.voice) seated.add(r.voice.sessionId)
  for (const p of peerRails) if (p.state === "open" && p.voice) seated.add(p.voice.sessionId)

  const homed = new Map<Rail, MountChip[]>() // host → guests, in seating order
  const seatGuest = (host: Rail | null, chip: MountChip) => {
    if (!host || !host.voice) return
    const list = homed.get(host) ?? []
    list.push(chip)
    homed.set(host, list)
    seated.add(chip.id)
  }

  // (a) Folded chain levels: the spine's own session re-homes.
  chainRails.forEach((rail, idx) => {
    if (rail.state !== "spine") return
    const r = byId.get(rail.id)
    if (!r || !PRIMITIVE.has(r.kind) || seated.has(r.id)) return
    const host = hostOfChainIndex(idx)
    if (!host?.voice) return
    seatGuest(
      host,
      chipOf(
        recs,
        r,
        seenCut,
        `one thread, one place: ${r.label.toLowerCase()}'s rail folded to a spine, so the thread re-homed here. speaking here speaks to ${handOf(r) ?? r.label.toLowerCase()}.`,
      ),
    )
  })

  // Index the current seats once for peer and loose-session re-homing.
  const hostBySession = new Map<string, Rail>()
  for (const r of chainRails) if (r.state === "open" && r.voice) hostBySession.set(r.voice.sessionId, r)
  for (const p of peerRails) if (p.state === "open" && p.voice) hostBySession.set(p.voice.sessionId, p)

  // (b) A capacity-refused peer re-homes too. Popping out changes its priority,
  //     never its reachability and never the number of physical pixels.
  for (const peer of peerRails) {
    if (peer.state !== "spine" || !peer.addressedTabId) continue
    const record = byId.get(peer.addressedTabId)
    if (!record || seated.has(record.id)) continue
    let host: Rail | null = null
    let cur = record.parentId
    let guard = 0
    while (cur && guard++ < 64) {
      const candidate = hostBySession.get(cur)
      if (candidate) {
        host = candidate
        break
      }
      cur = byId.get(cur)?.parentId ?? null
    }
    seatGuest(
      host ?? openAny[0] ?? null,
      chipOf(
        recs,
        record,
        seenCut,
        `one thread, one place: ${record.label.toLowerCase()}'s peer rail exceeded the measured room, so the thread re-homed here.`,
      ),
    )
  }

  // (c) Engaged primitives anywhere in the map with no seat: nearest open rail
  //     on their ancestor path; failing that, the first open rail.
  const engagedLoose = recs
    .filter((r) => PRIMITIVE.has(r.kind) && engagementOf(r) === "engaged" && !seated.has(r.id))
    .sort((a, b) => a.bornSeq - b.bornSeq)
  for (const r of engagedLoose) {
    let host: Rail | null = null
    let cur = r.parentId
    let guard = 0
    while (cur && guard++ < 64) {
      const h = hostBySession.get(cur)
      if (h) {
        host = h
        break
      }
      cur = byId.get(cur)?.parentId ?? null
    }
    seatGuest(
      host ?? openAny[0] ?? null,
      chipOf(
        recs,
        r,
        seenCut,
        `one thread, one place: no room for ${(handOf(r) ?? r.label).toLowerCase()}'s rail here — this rail is its seat. speaking here speaks to ${handOf(r) ?? r.label.toLowerCase()}.`,
      ),
    )
  }

  // Chips: engaged guests first, then arrival order; two render, the rest are
  // the +N census (018 §6 — at most two chips, then the census chip).
  for (const [host, guests] of homed) {
    const ordered = [...guests].sort((a, b) => {
      const ea = a.engagement === "engaged" ? 0 : 1
      const eb = b.engagement === "engaged" ? 0 : 1
      return ea - eb
    })
    host.voice!.mounts = ordered.slice(0, 2)
    host.voice!.census = ordered.slice(2)
  }

  const rails = [...chainRails, ...peerRails]
  const openCount = rails.filter((r) => r.state === "open").length
  return { rails, chain, leafId, budget, openCount, total: rails.length }
}
